import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from './config'

interface RetryConfig extends InternalAxiosRequestConfig {
	_retry?: boolean
}

const instance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
})

let isRefreshing = false
const failedQueue: Array<{
	resolve: () => void
	reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error)
		} else {
			prom.resolve()
		}
	})
	failedQueue.length = 0
}

instance.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as RetryConfig

		// Don't intercept if it's not a 401, already retried, or is the refresh endpoint itself
		if (
			error.response?.status !== 401 ||
			originalRequest?._retry ||
			originalRequest?.url?.includes('/auth/refresh')
		) {
			return Promise.reject(error)
		}

		// Queue concurrent requests while a refresh is in progress
		if (isRefreshing) {
			return new Promise<void>((resolve, reject) => {
				failedQueue.push({ resolve, reject })
			})
				.then(() => instance(originalRequest))
				.catch((err) => Promise.reject(err))
		}

		originalRequest._retry = true
		isRefreshing = true

		try {
			// Call refresh — backend sets new cookies via Set-Cookie
			await axios.get('/api/v1/auth/refresh', {
				baseURL: API_BASE_URL,
				withCredentials: true
			})
			processQueue(null)
			// Retry the original request with the new access cookie
			return instance(originalRequest)
		} catch (refreshError) {
			// Refresh failed (400/404/500) — redirect to login
			processQueue(refreshError)
			window.location.href = '/login'
			return Promise.reject(refreshError)
		} finally {
			isRefreshing = false
		}
	}
)

export default instance
